import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCheck, Mic, RefreshCw, Undo2, X } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateHeure } from "@/lib/format";
import { ouvrirMicro } from "@/components/shell/useDockAction";
import { CarteAction, STATUTS_IA, signalerMaj } from "./CarteAction";
import { Autopsie, AppelsAReecouter } from "./Direction";
import { BoutonVisio } from "@/components/visio/BoutonVisio";

const Bloc = ({ titre, testId, children }) => (
  <section className="at-card p-5 space-y-3 min-w-0" data-testid={testId}>
    <h2 className="text-base md:text-lg font-bold">{titre}</h2>
    {children}
  </section>
);

const Briefing = ({ b, onRegenerer }) => (
  <Bloc titre={`Briefing du matin · ${b.titre}`} testId="assistant-briefing">
    <p className="text-sm break-words" data-testid="assistant-briefing-synthesis">{b.synthese}</p>
    <ul className="space-y-1">
      {b.priorites.map((p, i) => (
        <li key={i}><Link to={p.lien} className="text-sm font-semibold min-h-[44px] flex items-center underline-offset-2 hover:underline" data-testid={`assistant-priority-${i}`}>{p.texte}</Link></li>
      ))}
    </ul>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <p className="text-xs at-muted">{b.modele}{b.ia_reelle ? "" : " (simulation)"} · {formatDateHeure(b.created_at)}{b.ia_reelle ? "" : ` · ${b.whatsapp}`}</p>
      <button type="button" onClick={onRegenerer} className="at-btn at-btn-ghost" data-testid="assistant-briefing-regenerate"><RefreshCw size={15} />Régénérer</button>
    </div>
  </Bloc>
);

const Propositions = ({ liste, onMaj }) => {
  const [choix, setChoix] = useState([]);
  const tous = choix.length === liste.length && liste.length > 0;
  const groupe = async () => {
    try {
      const { data } = await api.post("/assistant/actions/groupe", { ids: choix });
      toast.success(`${data.faites} action${data.faites > 1 ? "s" : ""} validée${data.faites > 1 ? "s" : ""}.`);
      data.erreurs.forEach((e) => toast.error(e.erreur));
      setChoix([]); signalerMaj(); onMaj();
    } catch (e) { toast.error(messageErreur(e)); }
  };
  const refuser = async (id) => { try { await api.post(`/assistant/actions/${id}/refuser`); onMaj(); } catch (e) { toast.error(messageErreur(e)); } };
  const basculer = (id) => setChoix((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  return (
    <Bloc titre={`Ce qu'elle propose (${liste.length})`} testId="assistant-proposals">
      {liste.length === 0 && <p className="text-sm at-muted" data-testid="assistant-proposals-empty">Aucune proposition à valider.</p>}
      {liste.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <label className="at-soft min-h-[44px] px-3 flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" className="w-5 h-5" checked={tous} onChange={() => setChoix(tous ? [] : liste.map((p) => p.id))} style={{ accentColor: "var(--accent)" }} data-testid="assistant-select-all" />Tout sélectionner
          </label>
          <button type="button" disabled={!choix.length} onClick={groupe} className="at-btn at-btn-primary" data-testid="assistant-validate-selection"><CheckCheck size={16} />Valider la sélection ({choix.length})</button>
        </div>
      )}
      {liste.map((p) => (
        <div key={p.id} className="flex gap-2 items-start min-w-0">
          <input type="checkbox" className="w-5 h-5 mt-4 shrink-0" checked={choix.includes(p.id)} onChange={() => basculer(p.id)} style={{ accentColor: "var(--accent)" }} aria-label="Sélectionner" data-testid={`assistant-select-${p.id}`} />
          <div className="flex-1 min-w-0"><CarteAction action={p} onFait={onMaj} /></div>
          <button type="button" onClick={() => refuser(p.id)} aria-label="Refuser" className="at-icon-btn w-11 h-11 shrink-0 mt-2" data-testid={`assistant-refuse-${p.id}`}><X size={16} /></button>
        </div>
      ))}
    </Bloc>
  );
};

const LigneJournal = ({ a, onAnnuler }) => (
  <li className="at-soft p-3 flex items-start justify-between gap-2 min-w-0" data-testid={`assistant-log-${a.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold break-words">{a.resume}</p>
      <p className="text-xs at-muted">{STATUTS_IA[a.statut]}{a.mode === "auto" ? " · fait seule" : ""}{a.canal === "whatsapp" ? " · via WhatsApp" : ""} · {a.user_nom} · {formatDateHeure(a.execute_le || a.created_at)}{a.erreur ? ` · ${a.erreur}` : ""}</p>
    </div>
    {a.statut === "fait" && a.annulable && (
      <button type="button" onClick={() => onAnnuler(a.id)} className="at-btn at-btn-ghost shrink-0" data-testid={`assistant-undo-${a.id}`}><Undo2 size={15} />Annuler</button>
    )}
  </li>
);

export const MonAssistant = () => {
  const { user } = useAuth();
  const partenaire = user.role === "partenaire";
  const [d, setD] = useState(null);
  const [journal, setJournal] = useState([]);
  const charger = useCallback(() => {
    api.get("/assistant").then((r) => setD(r.data)).catch(() => {});
    api.get("/assistant/journal").then((r) => setJournal(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    charger();
    window.addEventListener("assistant:maj", charger);
    return () => window.removeEventListener("assistant:maj", charger);
  }, [charger]);
  const regenerer = async () => { try { await api.post("/assistant/briefing"); charger(); toast.success("Briefing régénéré."); } catch (e) { toast.error(messageErreur(e)); } };
  const annuler = async (id) => { try { await api.post(`/assistant/actions/${id}/annuler`); toast.success("Action annulée."); charger(); } catch (e) { toast.error(messageErreur(e)); } };
  if (!d) return null;
  return (
    <div className="space-y-4 min-w-0" data-testid="my-assistant">
      <button type="button" onClick={() => ouvrirMicro(partenaire ? { ecoute: true } : undefined)} className={`at-micro w-full ${partenaire ? "min-h-[88px] text-lg" : "min-h-[52px]"} flex items-center justify-center gap-2 font-bold`} data-testid="assistant-open-panel"><Mic size={partenaire ? 28 : 18} />{partenaire ? "Dites ce qui a avancé" : "Parler à l'assistant"}</button>
      <Briefing b={d.briefing} onRegenerer={regenerer} />
      <BoutonVisio testId="assistant-visio-button" className="at-btn at-btn-ghost w-full" />
      {user.role === "dirigeant" && <Autopsie peutLancer />}
      {["dirigeant", "responsable"].includes(user.role) && <AppelsAReecouter />}
      <Propositions liste={d.propositions} onMaj={charger} />
      <Bloc titre="Ce qu'elle a fait" testId="assistant-done">
        {d.faites.length === 0 ? <p className="text-sm at-muted">Rien pour l'instant.</p> : (
          <ul className="space-y-2">{d.faites.slice(0, 5).map((a) => <LigneJournal key={a.id} a={a} onAnnuler={annuler} />)}</ul>
        )}
      </Bloc>
      <Bloc titre={`Ce qui reste à faire (${d.a_faire.length})`} testId="assistant-todo">
        {d.a_faire.length === 0 ? <p className="text-sm at-muted">Aucune tâche aujourd'hui.</p> : (
          <ul className="space-y-1">{d.a_faire.map((t) => (
            <li key={t.id}><Link to={t.fiche_id ? `/fiches/${t.fiche_id}` : "/agenda"} className="min-h-[44px] flex items-center justify-between gap-2 text-sm" data-testid={`assistant-todo-${t.id}`}>
              <span className="truncate"><b>{t.type_label}</b> · {t.fiche_nom}</span>
              <span className="shrink-0 text-xs font-semibold" style={{ color: t.en_retard ? "var(--danger)" : "var(--muted)" }}>{formatDateHeure(t.echeance)}</span>
            </Link></li>
          ))}</ul>
        )}
      </Bloc>
      <Bloc titre={`Journal de l'IA (${journal.length})`} testId="assistant-log">
        {journal.length === 0 ? <p className="text-sm at-muted">Aucune action de l'IA pour l'instant.</p> : (
          <ul className="space-y-2">{journal.map((a) => <LigneJournal key={a.id} a={a} onAnnuler={annuler} />)}</ul>
        )}
      </Bloc>
    </div>
  );
};
